import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import type { ComboboxOption } from '../../components/Combobox';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import type { MacroAction } from '../shortcuts/api';
import { TicketCombobox } from '../tickets/TicketCombobox';
import { ActionEditor } from './ActionEditor';
import { ConditionEditor } from './ConditionEditor';
import { type ConditionGroup } from './conditions';
import { ConditionValueText } from './ConditionValueInput';
import { useCreateRule, useDeleteRule, useRules, useTestRule, useUpdateRule } from './queries';
import { Select } from '../../components/Select';

/**
 * Regeln -- F3-11, F3-12, K-58.
 *
 * Die Liste steht in der Reihenfolge, in der die Regeln laufen. Das ist keine
 * Sortierlaune: Bei `stop_after_match` haengt das Ergebnis daran, und eine
 * Ansicht, die anders sortierte als der Lauf, waere irrefuehrend.
 *
 * Der Testlauf steht neben jeder Regel, weil er dorthin gehoert: Wer eine
 * Regel schreibt, will sie an einem echten Ticket ausprobieren, bevor sie auf
 * hundert wirkt -- und er soll dafuer kein Ticket opfern muessen. Der Server
 * aendert beim Test nichts (docs/06, F3-12).
 */
const TRIGGERS = ['ticket_created', 'ticket_updated', 'article_added'] as const;

const EMPTY_CONDITION: ConditionGroup = { operator: 'and', rules: [] };

export function RulesPanel() {
  const { t } = useTranslation();
  const rules = useRules();
  const create = useCreateRule();
  const update = useUpdateRule();
  const remove = useDeleteRule();
  const test = useTestRule();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [trigger, setTrigger] = useState<(typeof TRIGGERS)[number]>('ticket_created');
  const [conditions, setConditions] = useState<ConditionGroup>(EMPTY_CONDITION);
  const [actions, setActions] = useState<MacroAction[]>([]);
  const [stopAfterMatch, setStopAfterMatch] = useState(false);

  const [testFor, setTestFor] = useState<string | null>(null);
  const [testTicket, setTestTicket] = useState<ComboboxOption | null>(null);

  const submit = () => {
    create.mutate(
      {
        name: name.trim(),
        description: description.trim(),
        trigger_event: trigger,
        conditions: conditions.rules.length > 0 ? conditions : {},
        actions,
        is_active: true,
        sort_order: 0,
        stop_after_match: stopAfterMatch,
      },
      {
        onSuccess: () => {
          setName('');
          setDescription('');
          setConditions(EMPTY_CONDITION);
          setActions([]);
          setStopAfterMatch(false);
        },
      },
    );
  };

  return (
    <div className="stack">
      <section className="card">
        <h2>{t('automation.newRule')}</h2>

        <div className="field">
          <label className="field__label" htmlFor="rule-name">
            {t('masterdata.name')}
          </label>
          <input
            id="rule-name"
            className="field__control"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="rule-description">
            {t('templates.description')}
          </label>
          <input
            id="rule-description"
            className="field__control"
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="rule-trigger">
            {t('automation.trigger')}
          </label>
          <Select
            id="rule-trigger"
            className="field__control"
            value={trigger}
            onChange={(event) => {
              setTrigger(event.target.value as (typeof TRIGGERS)[number]);
            }}
          >
            {TRIGGERS.map((value) => (
              <option key={value} value={value}>
                {t(`automation.triggerEvent.${value}`)}
              </option>
            ))}
          </Select>
        </div>

        <h3>{t('automation.conditions')}</h3>
        <ConditionEditor
          value={conditions}
          onChange={setConditions}
          emptyHint={t('automation.emptyConditionHint')}
        />

        <h3>{t('macros.actions')}</h3>
        <ActionEditor value={actions} onChange={setActions} />

        <div className="field">
          <label className="field__checkbox" htmlFor="rule-stop">
            <input
              id="rule-stop"
              type="checkbox"
              checked={stopAfterMatch}
              onChange={(event) => {
                setStopAfterMatch(event.target.checked);
              }}
            />
            {t('automation.stopAfterMatch')}
          </label>
        </div>

        {create.error ? <ErrorNotice error={create.error} /> : null}

        <div className="row">
          <Button
            variant="primary"
            disabled={name.trim() === '' || actions.length === 0 || create.isPending}
            onClick={submit}
          >
            {t('masterdata.add')}
          </Button>
        </div>
      </section>

      <section className="card">
        <h2>{t('automation.rules')}</h2>

        {rules.isPending ? <LoadingIndicator /> : null}
        {rules.error ? <ErrorNotice error={rules.error} /> : null}

        {rules.data?.length === 0 ? (
          <p className="table__muted">{t('automation.noRules')}</p>
        ) : null}

        <ul className="task-list">
          {(rules.data ?? []).map((rule) => (
            <li key={rule.id} className="task-list__item task-list__item--column">
              <div className="row">
                <strong>{rule.name}</strong>
                <Badge tone={rule.is_active ? 'success' : 'neutral'}>
                  {rule.is_active ? t('automation.active') : t('automation.inactive')}
                </Badge>
                <Badge tone="info">{t(`automation.triggerEvent.${rule.trigger_event}`)}</Badge>
                <span className="table__muted">
                  {t('automation.matchCount', { count: rule.match_count })}
                </span>
              </div>

              {rule.description ? <p className="table__muted">{rule.description}</p> : null}

              <div className="row">
                <Button
                  variant="ghost"
                  onClick={() => {
                    update.mutate({
                      ruleId: rule.id,
                      payload: { is_active: !rule.is_active },
                    });
                  }}
                >
                  {rule.is_active ? t('automation.deactivate') : t('automation.activate')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setTestFor(testFor === rule.id ? null : rule.id);
                    test.reset();
                  }}
                >
                  {t('automation.test')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (window.confirm(t('masterdata.deleteConfirm'))) {
                      remove.mutate(rule.id);
                    }
                  }}
                >
                  {t('masterdata.delete')}
                </Button>
              </div>

              {testFor === rule.id ? (
                <div className="stack">
                  <p className="field__hint">{t('automation.testHint')}</p>
                  <div className="row">
                    {/* Das Ticket wird gesucht und nicht als Kennung abgeschrieben
                        -- die Kennung steht nur in der Adresszeile. */}
                    <TicketCombobox
                      value={testTicket}
                      onChange={setTestTicket}
                      ariaLabel={t('automation.ticketId')}
                    />
                    <Button
                      variant="secondary"
                      disabled={testTicket === null || test.isPending}
                      onClick={() => {
                        if (testTicket === null) return;
                        test.mutate({ ruleId: rule.id, ticketId: testTicket.id });
                      }}
                    >
                      {t('automation.runTest')}
                    </Button>
                  </div>

                  {test.error ? <ErrorNotice error={test.error} /> : null}

                  {test.data ? (
                    <div className="stack">
                      <Badge tone={test.data.matched ? 'success' : 'warning'}>
                        {test.data.matched
                          ? t('automation.testMatched')
                          : t('automation.testNotMatched')}
                      </Badge>

                      <ul className="task-list">
                        {test.data.conditions.map((outcome, index) => (
                          <li key={`${outcome.field}-${String(index)}`} className="task-list__item">
                            <Badge tone={outcome.matched ? 'success' : 'danger'}>
                              {outcome.matched ? '✓' : '✗'}
                            </Badge>
                            <span>
                              {outcome.field} {t(`automation.op.${outcome.op}`)}{' '}
                              <ConditionValueText field={outcome.field} value={outcome.value} />
                            </span>
                            <span className="table__muted">
                              {t('automation.actualValue', { value: outcome.actual ?? '—' })}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {(test.data.would_apply ?? []).length > 0 ? (
                        <p>
                          {t('automation.wouldApply')}: {(test.data.would_apply ?? []).join(', ')}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
